import { combineReducers, configureStore } from '@reduxjs/toolkit';
import dossierReducer from './data/dossier';
import signRoomReducer from './data//sign-room';
import userReducer from './data/user';
import adminConsoleReducer from './features/admin-console/admin-console-slice';
import customerServiceReducer from './features/admin-console/customer-service/customer-service-slice';
import fiduciarieReducer from './features/admin-console/fiduciarie/fiduciarie-slice';
import azimutForYouReducer from './features/azimut-for-you/azimut-for-you-slice';
import documentsCommonReducer from './features/documents/common/common-slice';
import communicationsDocumentsSlice from './features/documents/communications/communications-slice';
import documentsListSlice from './features/documents/documents-list/documents-list-slice';
import offeringDocumentsSlice from './features/documents/offerings/offerings-slice';
import homeReducer from './features/home/home-slice';
import newsReducer from './features/news/news-slice';
import changeEmailReducer from './features/profile/change-email/change-email-slice';
import changePasswordReducer from './features/profile/change-password/change-password-slice';
import changePhoneNumberReducer from './features/profile/change-phone-number/change-phone-number-slice';
import signatureAreaReducer from './features/profile/signature-area/signature-area-slice';
import firstAccessReducer from './features/user-access/first-access/first-access-slice';
import loginReducer from './features/user-access/login/login-slice';
import retrievePasswordReducer from './features/user-access/retrieve-password/retrieve-password-slice';
import retrieveUsernameReducer from './features/user-access/retrieve-username/retrieve-username-slice';
import unlockUserReducer from './features/user-access/unlock-user/unlock-user-slice';
import userRegistrationReducer from './features/user-access/user-registration/user-registration-slice';
import walletCommonReducer from './features/wallet/common/common-slice';
import myWalletReducer from './features/wallet/my-wallet/my-wallet-slice';
import sharedAccountsReducer from './features/wallet/my-wallet/shared-accounts-slice';
import openidReducer from './features/openid/openid-slice';
import pirboxReducer from './features/pirbox/pir-box-slices';
import { ACTION_LOGOUT, ACTION_REHYDRATE_USER, ACTION_REHYDRATE_ADMIN_USER } from './constants';
import referralCodeReducer from './features/referral-code/referral-code-slice';
import dobUtilityReducer from './features/dob/utility/utility-slice';
import mifidSurveyReducer from './features/home/onboarding-self/mifid-survey-slice';
import invydioSessionReducer from './features/invydio-session/invydio-session-slice';
import amlFatcaCrsSurveyReducer from './features/home/onboarding-self/home-onboarding-self-aml-fatca-crs-survey-slice';
import finalizeOnboardingReducer from './features/finalize-onboarding/finalize-onboarding-slice';
import webCollaborationReducer from './features/web-collaboration/web-collaboration-slice';
import { enableMapSet } from 'immer';

enableMapSet();

const combinedReducer = combineReducers({
  data: combineReducers({
    user: userReducer,
    dossier: dossierReducer,
    signRoom: signRoomReducer,
  }),
  features: combineReducers({
    login: loginReducer,
    firstAccess: firstAccessReducer,
    retrieveUsername: retrieveUsernameReducer,
    retrievePassword: retrievePasswordReducer,
    userRegistration: userRegistrationReducer,
    unlockUser: unlockUserReducer,
    changePassword: changePasswordReducer,
    changeEmail: changeEmailReducer,
    changePhoneNumber: changePhoneNumberReducer,
    signatureArea: signatureAreaReducer,
    adminConsole: combineReducers({
      common: adminConsoleReducer,
      customerService: customerServiceReducer,
      fiduciarie: fiduciarieReducer,
    }),
    walletStore: combineReducers({
      common: walletCommonReducer,
      myWallet: myWalletReducer,
      sharedAccounts: sharedAccountsReducer,
    }),
    documents: combineReducers({
      common: documentsCommonReducer,
      documentsList: documentsListSlice,
      offerings: offeringDocumentsSlice,
      communications: communicationsDocumentsSlice,
    }),
    news: newsReducer,
    azimutForYou: azimutForYouReducer,
    home: homeReducer,
    openId: openidReducer,
    pirbox: pirboxReducer,
    referralCode: referralCodeReducer,
    invydioSession: invydioSessionReducer,
    mifidSurvey: mifidSurveyReducer,
    amlSurvey: amlFatcaCrsSurveyReducer,
    dob: combineReducers({
      utility: dobUtilityReducer,
    }),
    finalizeOnboarding: finalizeOnboardingReducer,
    webCollaboration: webCollaborationReducer,
  }),
});

export const rootReducer = (state, action) => {
  if (action.type === ACTION_LOGOUT) {
    state = undefined;
  }
  if (action.type === ACTION_REHYDRATE_USER) {
    state = { ...state, data: { ...state.data, user: action.payload } };
  }
  if (action.type === ACTION_REHYDRATE_ADMIN_USER) {
    state = {
      ...state,
      features: {
        ...state?.features,
        adminConsole: {
          ...state?.features?.adminConsole,
          common: {
            ...state?.features?.adminConsole?.common,
            user: action.payload,
          },
        },
      },
    };
  }
  return combinedReducer(state, action);
};

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }),
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<any>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = any;
